import { atom, useAtomValue, useSetAtom } from 'jotai'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Logging } from 'sierra-client/core/logging'
import { useShouldShowPrompt } from 'sierra-client/editor/blocks/paragraph/use-should-show-prompt'
import { useSlashMenuState } from 'sierra-client/editor/editor-jotai-context'
import { useDebouncedAndLiveState } from 'sierra-client/hooks/use-debounced-state'
import { useIsDebugMode } from 'sierra-client/hooks/use-is-debug-mode'
import { useOnMount } from 'sierra-client/hooks/use-on-mount'
import { useIsUnique } from 'sierra-client/hooks/use-uniqueness-check'
import { useCachedQuery } from 'sierra-client/state/api'
import { useDispatch } from 'sierra-client/state/hooks'
import { useEditorReadOnly } from 'sierra-client/views/v3-author/context'
import { useElement } from 'sierra-client/views/v3-author/element-context'
import { getNodesInRange } from 'sierra-client/views/v3-author/paste/with-copy'
import { printNodeToMd } from 'sierra-client/views/v3-author/print-nodes-as-markdown'
import { rangeOfNode } from 'sierra-client/views/v3-author/queries'
import { setHandleAcceptCompletionKeyDownCallback } from 'sierra-client/views/v3-author/use-on-key-down'
import { createNanoId12FromString } from 'sierra-domain/nanoid-extensions'
import { XRealtimeAuthorGenerateContinueWriting } from 'sierra-domain/routes'
import { isCurrentSelectionCollapsed } from 'sierra-domain/slate-util'
import { CustomElementType } from 'sierra-domain/v3-author'
import { Editor, Path, Point, Range, Text } from 'slate'
import { ReactEditor, useSelected, useSlateSelector, useSlateStatic } from 'slate-react'
import styled from 'styled-components'

const CompletionSpan = styled.span.attrs({ contentEditable: false })`
  opacity: 0.3;
  user-select: none;

  cursor: pointer;
  :hover {
    opacity: 0.6;
  }
`

function findLatestWords(editor: Editor, n: number): string {
  const { selection } = editor
  if (selection === null || !Range.isCollapsed(selection)) return ''
  const { focus } = selection
  const anchor = Editor.start(editor, [])
  const range = { anchor, focus }

  // Start from the user's cursor and move backwards one word at a time until the desired number of words have been collected
  let distance = n
  const start = focus
  let end = start
  for (const point of Editor.positions(editor, { at: range, reverse: true, unit: 'word' })) {
    end = point
    if (distance === 0) break

    distance--
  }

  const range2 = { anchor: start, focus: end }
  const nodes = getNodesInRange(editor, range2)
  const mdResult = printNodeToMd(editor, nodes).replace(/\n*$/, '')
  return mdResult
}

const latestNewSelectedTextNodeAtom = atom<Text | undefined>(undefined)

const debounceTime = 1000
const wordCount = 50

const getSuggestionId = (context: string, suggestion: string): string => {
  return createNanoId12FromString(context + suggestion)
}

const StreamingCompletion: React.FC<{ leaf: Text }> = ({ leaf }) => {
  const isLatestNewSelectedTextNode = useAtomValue(latestNewSelectedTextNodeAtom) === leaf
  const editor = useSlateStatic()
  const dispatch = useDispatch()

  const [debouncedContext, liveContext, setLiveContext] = useDebouncedAndLiveState<{
    context: string
  }>({ context: '' }, { wait: debounceTime })
  const [initialLeaf] = useState(leaf)

  const hasEditedText = initialLeaf !== leaf

  const isLongEnough = debouncedContext.context.length > 10
  const queryEnabled =
    isLongEnough &&
    debouncedContext.context === liveContext.context &&
    (hasEditedText || isLatestNewSelectedTextNode) &&
    debouncedContext.context === liveContext.context

  const [completionSuggestion, setCompletionSuggestion] = useState<string | undefined>(undefined)
  const completionQuery = useCachedQuery(
    XRealtimeAuthorGenerateContinueWriting,
    { context: debouncedContext.context },
    { enabled: queryEnabled, retry: false, retryOnMount: false }
  )
  const queryResult = completionQuery.data?.completion

  const suggestionIdRef = useRef<string>('')
  suggestionIdRef.current = getSuggestionId(debouncedContext.context, queryResult ?? '')

  useEffect(() => {
    const suggestion = queryResult === '' ? undefined : queryResult
    setCompletionSuggestion(suggestion)
    if (suggestion !== undefined)
      void dispatch(Logging.inlineCompletion.autoCompleteSuggested({ suggestionId: suggestionIdRef.current }))
  }, [dispatch, queryResult])

  useEffect(() => {
    const context = findLatestWords(editor, wordCount)
    setCompletionSuggestion(undefined)
    setLiveContext({ context })
  }, [editor, leaf, setLiveContext])

  const acceptCompletion = useCallback(
    (source: 'click' | 'tab') => {
      if (completionSuggestion !== undefined) {
        editor.insertText(completionSuggestion)
        setCompletionSuggestion(undefined)
        void dispatch(
          Logging.inlineCompletion.autoCompleteAccepted({ suggestionId: suggestionIdRef.current, source })
        )
      }
    },
    [completionSuggestion, dispatch, editor]
  )

  useEffect(() => {
    if (completionSuggestion === undefined) return

    setHandleAcceptCompletionKeyDownCallback(evt => {
      if (!ReactEditor.isFocused(editor)) return

      switch (evt.key) {
        case 'Escape': {
          evt.preventDefault()
          evt.stopPropagation()
          setCompletionSuggestion(undefined)
          void dispatch(
            Logging.inlineCompletion.autoCompleteRejected({ suggestionId: suggestionIdRef.current })
          )
          break
        }
        case 'Tab': {
          evt.preventDefault()
          evt.stopPropagation()
          acceptCompletion('tab')
          break
        }
      }
    })

    return () => {
      setHandleAcceptCompletionKeyDownCallback(undefined)
    }
  }, [acceptCompletion, completionSuggestion, completionQuery.data, editor, setLiveContext, dispatch])

  const isDebug = useIsDebugMode()

  return (
    <CompletionSpan onClick={() => acceptCompletion('click')} contentEditable={false}>
      {queryEnabled && completionSuggestion}
      {isDebug && (
        <>
          {queryEnabled && completionQuery.isFetching && '⏳'}
          {!isLongEnough && '📏' + debouncedContext.context.length}
          {!hasEditedText && '🖊️'}
          {!queryEnabled && '😴'}
          {isLatestNewSelectedTextNode && '🆕'}
        </>
      )}
    </CompletionSpan>
  )
}

const StreamingCompletionUniqueGuard: FC<{ leaf: Text }> = ({ leaf }): JSX.Element | null => {
  const isUnique = useIsUnique('StreamingCompletion')

  if (!isUnique) {
    return null
  }

  return <StreamingCompletion leaf={leaf} />
}

function selectionIsAtEndOfNode(editor: Editor, path: Path): boolean {
  const { selection } = editor
  if (selection === null) return false

  if (!Range.isCollapsed(selection)) return false

  const [, end] = rangeOfNode(editor, path)
  const isAtEnd = Point.equals(Range.end(selection), end)

  return isAtEnd
}

const SupportedInlineCompletionTypes: CustomElementType[] = ['paragraph', 'heading', 'list-item']

export const InlineCompletion: React.FC<{ leaf: Text }> = ({ leaf }) => {
  const readOnly = useEditorReadOnly()
  const isSelected = useSelected()
  const slashMenuState = useSlashMenuState()
  const isCollapsedSelection = useSlateSelector(isCurrentSelectionCollapsed)
  const element = useElement()
  const shouldShowPrompt = useShouldShowPrompt({ element })
  const lastLeaf = element.children.at(-1)
  const isLastLeaf = leaf === lastLeaf
  const setLatestNewSelectedTextNode = useSetAtom(latestNewSelectedTextNodeAtom)
  const isSupportedCompletionType = SupportedInlineCompletionTypes.includes(element.type)
  const isSlashMenuActive = slashMenuState.type === 'active'

  const selectShouldShowInlineCompletion = useCallback(
    (editor: Editor) => {
      if (readOnly) return false
      if (!isSupportedCompletionType) return false
      if (shouldShowPrompt) return false
      if (!isLastLeaf) return false
      if (!isSelected) return false
      if (isSlashMenuActive) return false

      // We want to be able to support this case, but currently there is an issue
      // where slate inserts a <br> tag into empty leaves, which breaks the layout
      if (leaf.text === '') return false

      // Only show completion if selection is at end of current node
      try {
        const leafPath = ReactEditor.findPath(editor, leaf)
        const isAtEndOfNode = selectionIsAtEndOfNode(editor, leafPath)

        return isAtEndOfNode
      } catch (e) {
        return false
      }
    },
    [isLastLeaf, isSelected, isSupportedCompletionType, leaf, readOnly, shouldShowPrompt, isSlashMenuActive]
  )

  const showInlineCompletion = useSlateSelector(selectShouldShowInlineCompletion)

  useOnMount(() => {
    if (leaf.text === '' && isSelected && isCollapsedSelection === true) {
      setLatestNewSelectedTextNode(leaf)
    }
  })

  if (!showInlineCompletion) return null

  return <StreamingCompletionUniqueGuard leaf={leaf} />
}
